import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Calendar, Star, DollarSign, Users, TrendingUp, 
  MessageSquare, Bell, Settings, ChevronRight, Clock, 
  MapPin, Phone, Mail, Camera, Edit3, Plus
} from 'lucide-react';
import { suppliersAPI } from '../services/api';
import { BackHeader } from '../components/BackHeader';

type TabType = 'overview' | 'bookings' | 'reviews' | 'earnings' | 'profile' | 'settings';

interface Booking {
  id: string;
  client: string;
  service: string;
  date: string;
  time: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  address: string;
  phone: string;
  price: number;
}

interface Earning {
  id: string;
  client: string;
  service: string;
  date: string;
  amount: number;
  commission: number;
  net: number;
  status: 'pending' | 'paid';
}

export default function SupplierDashboard() {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  
  // Stats and data from API
  const [stats, setStats] = useState({
    totalBookings: 0,
    completedJobs: 0,
    rating: 0,
    totalReviews: 0,
    monthlyEarnings: 0,
    pendingPayments: 0,
    responseRate: 0,
  });

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setDashboardError(null);
    setPaymentsError(null);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      if (!condoId) { setBookings([]); setDashboardError('Este panel está disponible para cuentas de proveedor.'); return; }
      const response = await suppliersAPI.getDashboard(condoId);
      const payload = response.data?.data ?? response.data ?? {};
      const realData = Array.isArray(payload.requests) ? payload.requests : [];
      const dashboardStats = payload.stats ?? {};

      // The canonical dashboard contract returns { services, requests, stats }.
      setStats({
        totalBookings: Number(dashboardStats.total_requests ?? realData.length),
        completedJobs: Number(dashboardStats.completed_requests ?? realData.filter((b: any) => b.status === 'completed').length),
        rating: Number(dashboardStats.avg_rating ?? 0),
        totalReviews: Number(dashboardStats.total_ratings ?? 0),
        monthlyEarnings: 0,
        pendingPayments: 0,
        responseRate: 0,
      });
      setBookings(realData);
      try {
        const paymentsResponse = await suppliersAPI.getProviderPayments(condoId);
        const paymentsPayload = paymentsResponse.data?.data ?? paymentsResponse.data ?? [];
        setEarnings(Array.isArray(paymentsPayload) ? paymentsPayload : (paymentsPayload.payments || []));
      } catch (err: any) {
        setPaymentsError(err?.response?.data?.error || 'El historial de pagos no está disponible.');
        setEarnings([]);
      }
    } catch (err: any) {
      setBookings([]);
      setDashboardError(err?.response?.status === 403 || err?.response?.status === 404
        ? 'Este panel está disponible para cuentas de proveedor.'
        : 'No se pudo cargar la información del proveedor.');
      console.error('Error loading supplier data:', err);
    } finally {
      setLoading(false);
    }
  };

  const [earnings, setEarnings] = useState<Earning[]>([]);
  const [requestAction, setRequestAction] = useState<string | null>(null);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const condoId = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').condoId; } catch { return undefined; } })();
  const updateRequest = async (requestId: string, action: 'accept' | 'reject' | 'complete') => {
    if (!condoId) return;
    setRequestAction(requestId + action);
    try { await suppliersAPI.updateRequest(condoId, requestId, action); await loadData(); }
    catch (err: any) { setPaymentsError(err?.response?.data?.error || 'La acción no está disponible en este momento.'); }
    finally { setRequestAction(null); }
  };

  const menuItems = [
    { id: 'overview', label: 'Resumen', icon: LayoutDashboard },
    { id: 'bookings', label: 'Citas', icon: Calendar },
    { id: 'reviews', label: 'Reseñas', icon: Star },
    { id: 'earnings', label: 'Ganancias', icon: DollarSign },
    { id: 'profile', label: 'Mi Perfil', icon: Users },
    { id: 'settings', label: 'Configuración', icon: Settings },
  ];

  const getStatusBadge = (status: Booking['status']) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      confirmed: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400',
    };
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      completed: 'Completada',
      cancelled: 'Cancelada',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {dashboardError ? (
        <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-[var(--v-text-primary)] font-semibold">{dashboardError}</p>
        </div>
      ) : (
      <>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Trabajos este mes</p>
          <p className="text-3xl font-bold text-[var(--v-text-primary)] mt-1">{stats.completedJobs}</p>
          <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            +12% vs mes pasado
          </p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Calificación</p>
          <p className="text-3xl font-bold text-[var(--v-text-primary)] mt-1">{stats.rating}</p>
          <p className="text-[var(--v-text-tertiary)] text-xs mt-1">{stats.totalReviews} reseñas</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Ganancias</p>
          <p className="text-3xl font-bold text-green-400 mt-1">${stats.monthlyEarnings.toLocaleString()}</p>
          <p className="text-[var(--v-text-tertiary)] text-xs mt-1">Este mes</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Por cobrar</p>
          <p className="text-3xl font-bold text-yellow-400 mt-1">${stats.pendingPayments.toLocaleString()}</p>
          <p className="text-[var(--v-text-tertiary)] text-xs mt-1">Pagos pendientes</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
        <h3 className="font-semibold text-white mb-3">Acciones Rápidas</h3>
        <div className="grid grid-cols-2 gap-3">
          <button className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg text-sm font-medium">
            Ver Citas de Hoy
          </button>
          <button className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg text-sm font-medium">
            Actualizar Disponibilidad
          </button>
          <button className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-lg text-sm font-medium">
            Promocionar Servicios
          </button>
          <button className="bg-orange-600 hover:bg-orange-700 text-white p-3 rounded-lg text-sm font-medium">
            Ver Estadísticas
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] overflow-hidden">
        <div className="p-4 border-b border-[var(--v-border-light)] flex items-center justify-between">
          <h3 className="font-semibold text-white">Citas Recientes</h3>
          <button 
            onClick={() => setActiveTab('bookings')}
            className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
          >
            Ver todas <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="divide-y divide-[var(--v-border-light)]">
          {bookings.length === 0 ? (
            <p className="p-6 text-center text-[var(--v-text-secondary)]">Aún no hay solicitudes disponibles.</p>
          ) : bookings.slice(0, 3).map((booking) => (
            <div key={booking.id} className="p-4 flex items-center justify-between">
              <div>
                <p className="text-[var(--v-text-primary)] font-medium">{booking.client}</p>
                <p className="text-[var(--v-text-secondary)] text-sm">{booking.service}</p>
                <p className="text-[var(--v-text-tertiary)] text-xs">{booking.date} • {booking.time}</p>
              </div>
              <div className="text-right">
                {getStatusBadge(booking.status)}
                <p className="text-green-400 font-semibold mt-1">${booking.price}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Alert */}
      {bookings.length > 0 && <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-4 rounded-xl border border-blue-500/30">
        <div className="flex items-start gap-3">
          <Star className="w-6 h-6 text-yellow-400" />
          <div>
            <h4 className="font-semibold text-white">¡Excelente desempeño!</h4>
            <p className="text-[var(--v-text-secondary)] text-sm">
              Estás en el top 10% de proveedores en tu zona. Sigue así para obtener la insignia "Super Proveedor".
            </p>
          </div>
        </div>
      </div>}
      </>
      )}
    </div>
  );

  const renderBookings = () => (
    <div className="space-y-4">
      {/* Filter buttons — scrollable on mobile */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        <button className="flex-shrink-0 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">Todas</button>
        <button className="flex-shrink-0 px-4 py-2 bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] rounded-lg text-sm font-medium hover:bg-[var(--v-surface-3)]">Pendientes</button>
        <button className="flex-shrink-0 px-4 py-2 bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] rounded-lg text-sm font-medium hover:bg-[var(--v-surface-3)]">Confirmadas</button>
        <button className="flex-shrink-0 px-4 py-2 bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] rounded-lg text-sm font-medium hover:bg-[var(--v-surface-3)]">Completadas</button>
      </div>

      {!dashboardError && bookings.length === 0 ? (
        <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-[var(--v-text-primary)] font-semibold">Aún no hay solicitudes disponibles.</p>
        </div>
      ) : dashboardError ? (
        <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-[var(--v-text-primary)] font-semibold">{dashboardError}</p>
        </div>
      ) : bookings.map((booking) => (
        <div key={booking.id} className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="text-white font-semibold">{booking.client}</h4>
              <p className="text-blue-400 text-sm">{booking.service}</p>
            </div>
            {getStatusBadge(booking.status)}
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div className="flex items-center gap-2 text-[var(--v-text-secondary)]">
              <Calendar className="w-4 h-4" />
              {booking.date} • {booking.time}
            </div>
            <div className="flex items-center gap-2 text-[var(--v-text-secondary)]">
              <MapPin className="w-4 h-4" />
              {booking.address}
            </div>
            <div className="flex items-center gap-2 text-[var(--v-text-secondary)]">
              <Phone className="w-4 h-4" />
              {booking.phone}
            </div>
            <div className="flex items-center gap-2 text-green-400 font-semibold">
              <DollarSign className="w-4 h-4" />
              ${booking.price.toLocaleString()}
            </div>
          </div>

          <div className="flex gap-2">
            {booking.status === 'pending' && (
              <>
                <button onClick={() => updateRequest(booking.id, 'accept')} disabled={requestAction !== null} className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium">
                  {requestAction === booking.id + 'accept' ? 'Guardando...' : 'Confirmar'}
                </button>
                <button onClick={() => updateRequest(booking.id, 'reject')} disabled={requestAction !== null} className="flex-1 bg-red-600/20 hover:bg-red-600/30 disabled:opacity-50 text-red-400 py-2 rounded-lg text-sm font-medium">
                  Rechazar
                </button>
              </>
            )}
            {booking.status === 'confirmed' && (
              <button onClick={() => updateRequest(booking.id, 'complete')} disabled={requestAction !== null} className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium">
                Marcar como Completada
              </button>
            )}
            {booking.status === 'completed' && (
              <button className="w-full bg-[var(--v-surface-3)] hover:bg-[var(--v-surface-3)] text-[var(--v-text-primary)] py-2 rounded-lg text-sm font-medium">
                Ver Detalles
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderEarnings = () => (
    <div className="space-y-6">
      {paymentsError ? (
        <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-[var(--v-text-primary)] font-semibold">{paymentsError}</p>
        </div>
      ) : earnings.length === 0 ? (
        <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-[var(--v-text-primary)] font-semibold">Aún no hay pagos disponibles.</p>
        </div>
      ) : <>
      {/* Earnings Summary — single column on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-[var(--v-text-secondary)] text-sm">Total Facturado</p>
          <p className="text-2xl font-bold text-[var(--v-text-primary)]">$45,200</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-[var(--v-text-secondary)] text-sm">Comisiones</p>
          <p className="text-2xl font-bold text-red-400">-$2,260</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-[var(--v-text-secondary)] text-sm">Ganancia Neta</p>
          <p className="text-2xl font-bold text-green-400">$42,940</p>
        </div>
      </div>

      {/* Transactions */}
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] overflow-hidden">
        <div className="p-4 border-b border-[var(--v-border-light)]">
          <h3 className="font-semibold text-white">Transacciones Recientes</h3>
        </div>
        <div className="divide-y divide-[var(--v-border-light)]">
          {earnings.map((earning) => (
            <div key={earning.id} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-[var(--v-text-primary)] font-medium">{earning.client}</p>
                  <p className="text-[var(--v-text-secondary)] text-sm">{earning.service}</p>
                </div>
                <div className="text-right">
                  <p className="text-green-400 font-semibold">+${earning.net.toLocaleString()}</p>
                  <span className={`text-xs ${earning.status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
                    {earning.status === 'paid' ? '✓ Pagado' : '⏳ Pendiente'}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-[var(--v-text-tertiary)]">
                <span>{new Date(earning.date).toLocaleDateString()}</span>
                <span>Comisión: ${earning.commission}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Withdrawal Button */}
      <button className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2">
        <DollarSign className="w-6 h-6" />
        Retirar Ganancias (${stats.pendingPayments.toLocaleString()})
      </button>
      </>}
    </div>
  );

  const renderProfile = () => (
    <div className="space-y-6">
      {/* Profile Header — wraps on mobile */}
      <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-border-light)]">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
            JP
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-2xl font-bold text-white">Juan Pérez Plomería</h3>
            <p className="text-[var(--v-text-secondary)]">Plomería • Electricidad • Climatización</p>
            <div className="flex items-center gap-4 mt-2 flex-wrap">
              <span className="flex items-center gap-1 text-yellow-400">
                <Star className="w-4 h-4 fill-yellow-400" />
                4.8 (38 reseñas)
              </span>
              <span className="text-[var(--v-text-tertiary)] text-sm">•</span>
              <span className="text-green-400 text-sm">✓ Verificado</span>
              <span className="text-[var(--v-text-tertiary)] text-sm">•</span>
              <span className="text-blue-400 text-sm">Zona: 5km</span>
            </div>
          </div>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 w-full sm:w-auto">
            <Edit3 className="w-4 h-4" />
            Editar
          </button>
        </div>
      </div>

      {/* Stats — tighter gap on mobile */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-3xl font-bold text-[var(--v-text-primary)]">156</p>
          <p className="text-[var(--v-text-secondary)] text-sm">Trabajos</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-3xl font-bold text-[var(--v-text-primary)]">96%</p>
          <p className="text-[var(--v-text-secondary)] text-sm">Tasa de respuesta</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)] text-center">
          <p className="text-3xl font-bold text-[var(--v-text-primary)]">5 años</p>
          <p className="text-[var(--v-text-secondary)] text-sm">Experiencia</p>
        </div>
      </div>

      {/* Contact Info */}
      <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
        <h4 className="font-semibold text-white mb-4">Información de Contacto</h4>
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-[var(--v-text-primary)]">
            <Phone className="w-5 h-5 text-blue-400" />
            +58 412 123 4567
          </div>
          <div className="flex items-center gap-3 text-[var(--v-text-primary)]">
            <Mail className="w-5 h-5 text-blue-400" />
            juan@plomeriaexpress.com
          </div>
          <div className="flex items-center gap-3 text-[var(--v-text-primary)]">
            <MapPin className="w-5 h-5 text-blue-400" />
            Av. Principal, Caracas, Venezuela
          </div>
        </div>
      </div>

      {/* Photos — tighter gap on mobile */}
      <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
        <h4 className="font-semibold text-white mb-4">Trabajos Realizados</h4>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="aspect-square bg-[var(--v-surface-3)] rounded-lg flex items-center justify-center">
              <Camera className="w-8 h-8 text-[var(--v-text-tertiary)]" />
            </div>
          ))}
          <button className="aspect-square bg-[var(--v-surface-3)]/50 border-2 border-dashed border-[var(--v-border-strong)] rounded-lg flex flex-col items-center justify-center text-[var(--v-text-secondary)] hover:border-[var(--v-text-tertiary)]">
            <Plus className="w-6 h-6 mb-1" />
            <span className="text-xs">Agregar</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] min-w-0 overflow-x-hidden">
      <BackHeader fallback='/services/dashboard' />
      {/* Header */}
      <header className="bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
              JP
            </div>
            <div>
              <h1 className="text-lg font-bold text-[var(--v-text-primary)]">Panel de Proveedor</h1>
              <p className="text-[var(--v-text-secondary)] text-xs">Juan Pérez Plomería</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button className="relative p-2 text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
              Disponible
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Tab Navigation — horizontal scrollable bar */}
      <nav className="lg:hidden bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] overflow-x-auto scrollbar-hide -webkit-overflow-scrolling-touch">
        <div className="flex gap-1 px-2 py-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id as TabType)}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === item.id 
                  ? 'bg-blue-600 text-white' 
                  : 'text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)]'
              }`}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      <div className="flex min-w-0">
        {/* Sidebar — desktop only */}
        <aside className="hidden lg:block w-64 bg-[var(--v-surface-1)] border-r border-[var(--v-border-light)] min-h-[calc(100vh-73px)]">
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id as TabType)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition ${
                  activeTab === item.id 
                    ? 'bg-blue-600 text-white' 
                    : 'text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] hover:text-[var(--v-text-primary)]'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-4 sm:p-6">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'bookings' && renderBookings()}
          {activeTab === 'earnings' && renderEarnings()}
          {activeTab === 'profile' && renderProfile()}
          {(activeTab === 'reviews' || activeTab === 'settings') && (
            <div className="text-center py-12 text-[var(--v-text-tertiary)]">
              <p>🚧 Módulo en desarrollo</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
